import React, { useCallback, useEffect, useState, useMemo } from 'react';
import get from 'lodash/get';
import { useDispatch, useSelector } from 'react-redux';

import cloneDeep from 'lodash/cloneDeep';
import filter from 'lodash/filter';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import isEqual from 'lodash/isEqual';
import remove from 'lodash/remove';

import { Delete } from 'src/presentation/components/pages/shared/rowActions/Delete';
import { Edit } from 'src/presentation/components/pages/shared/rowActions/Edit';
import { getPreviewUrl } from './helpCenterHelpers';
import helpCenterResource, { Section, SiteConfig } from 'src/actions/helpCenter/helpCenterResource';
import HelpCenterSectionEditor from './HelpCenterSectionEditor';
import ExternalLink from '../shared/ExternalLink';
import { RootState } from 'src/actions/configuration/rootReducer';
import RouterLinkButton from 'src/presentation/components/RouterLinkButton';
import { STATUS_PERSISTING, STATUS_SUCCEEDED } from 'src/presentation/hooks/useResource';
import TablePage, { TablePageRender } from 'src/presentation/components/pages/shared/TablePage';
import useDocumentResource, { useResourceValue } from 'src/presentation/hooks/useDocumentResource';
import useRouter from 'src/presentation/hooks/useRouter';

const columnConfig = [
  {
    attr: 'header',
    label: 'Section',
  },
];

const PageDescription = ({ itemId }): JSX.Element => {
  const orgId = useSelector((state: RootState) => state.auth.orgId) || '';
  const previewUrl = getPreviewUrl(itemId, orgId);
  return (
    <>
      Arrange sections in the order you want them to appear in your Help Center.{''}
      <ExternalLink
        data-aid={`help-center-sections-page-preview-help-center-layout`}
        label="Preview Help Center layout"
        to={previewUrl}
      />
    </>
  );
};

function useRenderRowActions({ item }) {
  const onDelete = useOnDelete({ replace: helpCenterResource.effects.replace });
  return (
    <>
      <Edit id={item.id} />
      <Delete
        confirmationText="Delete this Section?"
        onDelete={onDelete}
        description="Deleting this section will immediately remove it from this Help Center."
        id={item.id}
        submitText="Delete Section"
      />
    </>
  );
}

type Matcher = { match: { url: string; params: { configId: string; id: string } } };

const HelpCenterSections: TablePageRender = ({ root }) => {
  const dispatch = useDispatch();
  const [sections, setSections] = useState<Section[]>([]);
  const [configs, setConfigs] = useState<SiteConfig[]>([]);
  const [title, setTitle] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const { match }: Matcher = useRouter();
  const { errors, loadingState, value: configuration, persistingState } = useDocumentResource(helpCenterResource);

  const [previousConfiguration, setPreviousConfiguration] = useState();

  const configurationHasChanged = useMemo(
    () => compareConfigs(configuration, previousConfiguration, match.params.configId),
    [configuration, previousConfiguration, match.params.configId]
  );

  const actions = (
    <RouterLinkButton data-aid={`addNewSection`} to={`${match.url}/new`}>
      Create Section
    </RouterLinkButton>
  );

  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (processing) {
      if (!isSaving && !isPersisting(persistingState)) {
        setProcessing(false);
        setPreviousConfiguration(configuration);
      }
    }
  }, [configuration, isSaving, persistingState, processing, setPreviousConfiguration, setProcessing]);

  useEffect(() => {
    if (!configurationHasChanged) {
      return;
    }

    if (loadingState === STATUS_SUCCEEDED) {
      if (!processing) {
        const config: SiteConfig = find(configuration.siteConfigs, {
          id: match.params.configId as string,
        } as SiteConfig);
        const helpCenterSections: Section[] = config.sections.map(s => ({
          ...s,
          lng: config.lng,
          audienceId: config.audienceId,
        }));

        setTitle(`${config.name} Sections`);
        setSections(helpCenterSections);
        setConfigs(configuration.siteConfigs);
      }

      setPreviousConfiguration(configuration);
    }
  }, [
    configuration,
    loadingState,
    setSections,
    setTitle,
    match.params.configId,
    setConfigs,
    configurationHasChanged,
    processing,
    setPreviousConfiguration,
  ]);

  function filterFunc(items, searchString) {
    if (!searchString) {
      return items;
    }
    const results = filter(items, item => {
      return item.header.toLowerCase().indexOf(searchString.toLowerCase()) > -1;
    });
    return results;
  }

  useEffect(() => {
    if (isSaving && !isPersisting(persistingState)) {
      dispatch(
        helpCenterResource.effects.replace({}, { siteConfigs: configs }, { navigateBack: false, doNotShowToast: true })
      );
      setIsSaving(false);
    }
  }, [configs, dispatch, isSaving, persistingState, setIsSaving, setSections]);

  const handleDragEnd = ({ currentItemId, replaceItemId }) => {
    const clonedConfig = cloneDeep({ siteConfigs: configs });

    const siteConfig = find(clonedConfig.siteConfigs, {
      id: match.params.configId as string,
    }) as SiteConfig;

    if (!siteConfig) {
      return;
    }

    moveItemToNewPosition({ sections: siteConfig.sections, currentItemId, replaceItemId });

    setSections(siteConfig.sections);
    setConfigs(clonedConfig.siteConfigs);
    setIsSaving(true);
    setProcessing(true);
  };

  function moveItemToNewPosition({ sections, currentItemId, replaceItemId }): void {
    const currentItemIndex = findIndex<Section>(sections, item => item.id === currentItemId);
    if (currentItemIndex === -1) {
      return;
    }
    const currentItem = cloneDeep(sections[currentItemIndex]);

    sections.splice(currentItemIndex, 1);

    const toIndex = findIndex<Section>(sections, item => item.id === replaceItemId);
    if (toIndex === -1) {
      return;
    }
    const updatedCurrentItemIndex = toIndex >= currentItemIndex ? toIndex + 1 : toIndex;

    sections.splice(updatedCurrentItemIndex, 0, currentItem);
  }

  const description = <PageDescription itemId={match.params.configId} />;

  return (
    <TablePage
      actions={actions}
      backButtonUrl="/help-center"
      backButtonLabel="Help Center"
      columnConfig={columnConfig}
      data-aid="help-center-sections-page"
      description={description}
      dragEndHandler={handleDragEnd}
      editor={HelpCenterSectionEditor}
      errors={errors}
      filterFunc={filterFunc}
      isLoading={loadingState !== 'succeeded'}
      items={sections}
      name={title}
      renderInPortal={true}
      renderRowActions={useRenderRowActions}
      resetErrors={helpCenterResource.actions.resetErrors}
      root={root}
    />
  );
};

function useOnDelete({ replace }) {
  const dispatch = useDispatch();
  const { value: helpCenterState } = useResourceValue(helpCenterResource);
  const {
    match: { params },
  }: {
    match: { params: { configId: string; id: string } };
  } = useRouter();
  return useCallback(
    ({ id }) => {
      const newState = cloneDeep(helpCenterState);
      const currentConfig: any = find(newState.siteConfigs, { id: params.configId as string } as any);
      remove(currentConfig.sections, (section: any) => section.id === id);
      dispatch(replace({}, newState));
    },

    [dispatch, helpCenterState, params.configId, replace]
  );
}

function isPersisting(persistingState) {
  return persistingState === STATUS_PERSISTING;
}

function compareConfigs(currentConfig, previousConfig, configId) {
  const currentConfiguration: SiteConfig = find(currentConfig.siteConfigs, {
    id: configId as string,
  } as SiteConfig);

  const prevConfig: SiteConfig | undefined = find(get(previousConfig, 'siteConfigs'), {
    id: configId as string,
  } as SiteConfig);

  return !isEqual(currentConfiguration, prevConfig);
}

export default HelpCenterSections;
